import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Sparkles,
    Smartphone,
    RotateCcw,
    Image,
    Code,
    ShoppingCart,
    UserPlus,
    Search,
    ChevronDown,
    X,
    Package,
    Loader2,
    Plus,
    Save
} from 'lucide-react';
import api, { categoriesApi } from '../services/api';
import { useWorkspace } from '../context/WorkspaceContext';
import DevicePicker from '../components/DevicePicker';
import PremiumBadge from '../components/PremiumBadge';
import AdvancedSettings from '../components/AdvancedSettings';
import './TestGoals.css';

function TestGoals() {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const [goalText, setGoalText] = useState('');
    const [platform, setPlatform] = useState('android');
    const [selectedDevice, setSelectedDevice] = useState({
        arn: 'auto',
        name: 'Auto (pick best device)',
        model: 'Automatic Selection',
        os: 'Best Available',
        platform: 'android',
        availability: 'AVAILABLE',
        provider: 'aws'
    });
    const [showDevicePicker, setShowDevicePicker] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [showAppPicker, setShowAppPicker] = useState(false);
    const [apps, setApps] = useState([]);
    const [turboMode, setTurboMode] = useState(false);
    const [geolocation, setGeolocation] = useState('');
    const [strictMode, setStrictMode] = useState(true);
    const [appsLoading, setAppsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [title, setTitle] = useState('');
    const [workspaceCategories, setWorkspaceCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;

    const loadCategories = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            const response = await categoriesApi.list(currentWorkspace.id);
            setWorkspaceCategories(response.data.categories || []);
        } catch (err) {
            console.error('Failed to load categories:', err);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);



    const exampleTemplates = [
        {
            icon: ShoppingCart,
            title: 'E-commerce Checkout',
            description: '"Add a pair of headphones to cart, proceed to checkout, and verify that the \'Guest Checkout\' option is available."'
        },
        {
            icon: UserPlus,
            title: 'User Registration',
            description: '"Register a new user with invalid email format and verify that the red error message appears below the input field."'
        },
        {
            icon: Search,
            title: 'Search & Filter',
            description: '"Search for \'Summer Dress\' and apply the \'Under $50\' filter. Ensure all results displayed are within the price range."'
        }
    ];

    // Load all apps for workspace (no platform filter - user picks app first)
    const loadApps = useCallback(async () => {
        if (!currentWorkspace?.id) return;

        setAppsLoading(true);
        try {
            const response = await api.get('/apps', {
                params: {
                    workspaceId: currentWorkspace.id,
                    limit: 50
                }
            });
            setApps(response.data.rows || []);
        } catch (err) {
            console.error('Failed to load apps:', err);
        } finally {
            setAppsLoading(false);
        }
    }, [currentWorkspace?.id]);

    useEffect(() => {
        loadApps();
    }, [loadApps]);

    const handleSave = async () => {
        if (!goalText.trim()) {
            setError('Please describe your test goal');
            return;
        }
        if (!selectedDevice) {
            setError('Please select a device');
            return;
        }
        if (!selectedApp) {
            setError('Please select an app to test');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await api.post('/tests/goals', {
                goalText,
                platform: selectedDevice.platform.toLowerCase(),
                appConfig: { 
                    appId: selectedApp.id,
                    turbo: turboMode,
                    geolocation: geolocation || null,
                    strict: strictMode
                },
                deviceProvider: selectedDevice.provider || 'aws',
                deviceArn: selectedDevice.arn,
                workspaceId: currentWorkspace?.id,
                title: title || undefined,
                categoryIds: selectedCategoryIds
            });

            navigate(`/goals/${response.data.testGoal.id}`);
        } catch (err) {
            let errorMessage = err.response?.data?.error || 'Failed to save test goal';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (isDraft = false) => {
        if (!goalText.trim()) {
            setError('Please describe your test goal');
            return;
        }
        if (!selectedDevice) {
            setError('Please select a device to run the test');
            return;
        }
        if (!selectedApp) {
            setError('Please select an app to test');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await api.post('/tests/execute', {
                goalText,
                platform: selectedDevice.platform.toLowerCase(),
                appConfig: { 
                    appId: selectedApp.id,
                    turbo: turboMode,
                    geolocation: geolocation || null,
                    strict: strictMode
                },
                deviceProvider: selectedDevice.provider || 'aws',
                deviceArn: selectedDevice.arn,
                workspaceId: currentWorkspace?.id,
                title: title || undefined,
                categoryIds: selectedCategoryIds
            });

            navigate(`/test/execution/${response.data.execution.id}`);
        } catch (err) {
            let errorMessage = err.response?.data?.error || 'Failed to submit test goal';

            if (errorMessage.includes("No available Device Farm devices")) {
                errorMessage += ' Try choosing a specific device and we will try to reserve it for you.'
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateClick = (template) => {
        setGoalText(template.description.replace(/"/g, ''));
    };

    const handleDeviceSelect = (device) => {
        setSelectedDevice(device);
        setShowDevicePicker(false);
    };

    const handleAppSelect = (app) => {
        setSelectedApp(app);
        // Set platform based on app and reset device selection
        const appPlatform = app.platform.toLowerCase();
        if (appPlatform !== platform) {
            setPlatform(appPlatform);
        }
        if (appPlatform === 'android' && !realDevicesEnabled) {
            setSelectedDevice({
                arn: 'emulator',
                name: 'Android Emulator',
                model: 'Automatic Selection',
                os: 'Best Available',
                platform: 'android',
                availability: 'AVAILABLE',
                provider: 'emulator'
            });
        } else {
            setSelectedDevice(null);
        }
        setShowAppPicker(false);
    };

    const handleReset = () => {
        setSelectedDevice(null);
        setSelectedApp(null);
        setPlatform('android');
        setGoalText('');
        setTitle('');
        setSelectedCategoryIds([]);
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(1)} MB`;
    };

    const getPlatformIcon = (platform) => {
        return platform === 'android' ? '🤖' : '🍎';
    };

    return (
        <div className="test-goals-page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Define Your Test Goal</h1>
                    <p className="page-subtitle">
                        Describe what you want to test in plain English. Our AI will automatically
                        translate your instructions into executable scripts across selected devices.
                    </p>
                </div>
            </div>

            {/* Main Goal Input Card */}
            <div className="goal-input-card">
                {/* App & Device Selection Row */}
                <div className="device-selection">
                    {/* App Selector - FIRST */}
                    <button
                        className="device-selector-btn"
                        onClick={() => setShowAppPicker(true)}
                    >
                        <Package size={14} />
                        {selectedApp ? (
                            <span className="selected-device-name">{selectedApp.displayName || selectedApp.name}</span>
                        ) : (
                            <span>1. Select App</span>
                        )}
                        <ChevronDown size={14} />
                    </button>

                    {/* Device Selector - SECOND (only enabled after app is selected) */}
                    <button
                        className={`device-selector-btn ${!selectedApp || (selectedApp.platform === 'android' && !realDevicesEnabled) ? 'disabled' : ''}`}
                        onClick={() => {
                            if (!selectedApp) return;
                            if (selectedApp.platform === 'android' && !realDevicesEnabled) return;
                            setShowDevicePicker(true);
                        }}
                        disabled={!selectedApp || (selectedApp.platform === 'android' && !realDevicesEnabled)}
                        title={!selectedApp ? 'Select an app first' : (selectedApp.platform === 'android' && !realDevicesEnabled) ? 'Standard emulator selected. Upgrade to access real devices.' : ''}
                    >
                        <Smartphone size={14} />
                        {selectedDevice ? (
                            <span className="selected-device-name">{selectedDevice.name}</span>
                        ) : (
                            <span>2. Select Device</span>
                        )}
                        {!realDevicesEnabled && <PremiumBadge title='Upgrade to run tests on real devices' />}
                        {realDevicesEnabled && <ChevronDown size={14} />}
                    </button>

                    <button className="reset-btn" onClick={handleReset}>
                        <RotateCcw size={14} />
                        Reset
                    </button>
                </div>

                {!realDevicesEnabled && selectedApp && selectedApp.platform === 'android' && (
                    <div className="emulator-warning-info" style={{ marginTop: '10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        ℹ️ Your test will run on <strong>Android Emulator</strong>. <Link to="/settings/billing" style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link> to run tests on real devices.
                    </div>
                )}

                {/* Selected Items Badges */}
                {(selectedDevice || selectedApp) && (
                    <div className="selection-summary">
                        {selectedDevice && (
                            <div className="selected-device-badge">
                                <Smartphone size={12} />
                                <span className="device-platform">{selectedDevice.platform}</span>
                                <span className="device-os-version">{selectedDevice.os}</span>
                                {selectedDevice.availability === 'AVAILABLE' ? (
                                    <span className="availability available">Available</span>
                                ) : (
                                    <span className="availability busy">Busy</span>
                                )}
                                <button className="remove-btn" onClick={() => setSelectedDevice(null)}>
                                    <X size={12} />
                                </button>
                            </div>
                        )}
                        {selectedApp && (
                            <div className="selected-device-badge">
                                <Package size={12} />
                                <span className="device-platform">{selectedApp.platform}</span>
                                {selectedApp.version && (
                                    <span className="device-os-version">v{selectedApp.version}</span>
                                )}
                                {selectedApp.fileSize && (
                                    <span className="device-os-version">{formatFileSize(selectedApp.fileSize)}</span>
                                )}
                                <button className="remove-btn" onClick={() => setSelectedApp(null)}>
                                    <X size={12} />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Metadata Row: Title and Categories */}
                <div className="goal-meta-row">
                    {/* Goal Title (Optional) */}
                    <div className="form-group flex-1">
                        <label className="form-label">Goal Title (Optional)</label>
                        <input
                            type="text"
                            className="goal-title-input"
                            placeholder="Give your test goal a short descriptive title..."
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    {/* Categories Autocomplete Select */}
                    <div className="form-group flex-1">
                        <label className="form-label">Categories</label>
                        <div className="tags-input-container">
                            <div className="tags-input-selected" onClick={() => document.querySelector('.tags-search-input')?.focus()}>
                                {selectedCategoryIds.map(id => {
                                    const cat = workspaceCategories.find(c => c.id === id);
                                    if (!cat) return null;
                                    return (
                                        <span
                                            key={id}
                                            className="category-tag-badge"
                                            style={{
                                                backgroundColor: cat.color ? `${cat.color}20` : '#94a3b820',
                                                color: cat.color || '#94a3b8',
                                                border: `1px solid ${cat.color || '#94a3b8'}40`
                                            }}
                                        >
                                            {cat.name}
                                            <X
                                                size={12}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedCategoryIds(prev => prev.filter(cId => cId !== id));
                                                }}
                                            />
                                        </span>
                                    );
                                })}
                                <input
                                    type="text"
                                    className="tags-search-input"
                                    placeholder={selectedCategoryIds.length === 0 ? "Select or create categories..." : ""}
                                    value={searchQuery}
                                    onFocus={() => setIsDropdownOpen(true)}
                                    onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const trimmed = searchQuery.trim();
                                            if (!trimmed) return;

                                            const matched = workspaceCategories.find(c => c.name.toLowerCase() === trimmed.toLowerCase());
                                            if (matched) {
                                                if (!selectedCategoryIds.includes(matched.id)) {
                                                    setSelectedCategoryIds(prev => [...prev, matched.id]);
                                                }
                                                setSearchQuery('');
                                                return;
                                            }

                                            try {
                                                const response = await categoriesApi.create({
                                                    name: trimmed,
                                                    color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')
                                                });
                                                const newCat = response.data.category;
                                                setWorkspaceCategories(prev => [...prev, newCat]);
                                                setSelectedCategoryIds(prev => [...prev, newCat.id]);
                                                setSearchQuery('');
                                            } catch (err) {
                                                console.error(err);
                                            }
                                        }
                                    }}
                                />
                            </div>

                            {isDropdownOpen && (
                                <div className="tags-dropdown-menu">
                                    {workspaceCategories
                                        .filter(cat => !selectedCategoryIds.includes(cat.id) && cat.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                        .map(cat => (
                                            <div
                                                key={cat.id}
                                                className="tags-dropdown-item"
                                                onMouseDown={() => {
                                                    setSelectedCategoryIds(prev => [...prev, cat.id]);
                                                    setSearchQuery('');
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '8px',
                                                        height: '8px',
                                                        borderRadius: '50%',
                                                        backgroundColor: cat.color || '#94a3b8',
                                                        marginRight: '8px'
                                                    }}
                                                />
                                                {cat.name}
                                            </div>
                                        ))
                                    }
                                    {searchQuery.trim() && !workspaceCategories.some(c => c.name.toLowerCase() === searchQuery.trim().toLowerCase()) && (
                                        <div
                                            className="tags-dropdown-item create-option"
                                            onMouseDown={async () => {
                                                const trimmed = searchQuery.trim();
                                                try {
                                                    const response = await categoriesApi.create({
                                                        name: trimmed,
                                                        color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')
                                                    });
                                                    const newCat = response.data.category;
                                                    setWorkspaceCategories(prev => [...prev, newCat]);
                                                    setSelectedCategoryIds(prev => [...prev, newCat.id]);
                                                    setSearchQuery('');
                                                } catch (err) {
                                                    console.error(err);
                                                }
                                            }}
                                        >
                                            <Plus size={12} style={{ marginRight: '6px' }} />
                                            Create category: <strong>"{searchQuery.trim()}"</strong>
                                        </div>
                                    )}
                                    {workspaceCategories.filter(cat => !selectedCategoryIds.includes(cat.id) && cat.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && !searchQuery.trim() && (
                                        <div className="tags-dropdown-no-results">
                                            No other categories. Type to create...
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Goal Text Area */}
                <textarea
                    className="goal-textarea"
                    value={goalText}
                    onChange={(e) => setGoalText(e.target.value)}
                    placeholder="Example: Log in as a 'premium' user, navigate to the settings page, change the notification preferences to 'Email Only', and verify the success toast message appears."
                    rows={6}
                />

                {/* Reusable Advanced Settings */}
                <AdvancedSettings
                    values={{ turbo: turboMode, geolocation, strict: strictMode }}
                    onChange={(newVal) => {
                        setTurboMode(newVal.turbo);
                        setGeolocation(newVal.geolocation);
                        setStrictMode(newVal.strict);
                    }}
                />

                {/* Bottom Bar */}
                {/* <div className="goal-input-footer">
                    <div className="footer-actions">
                        <button className="icon-btn" data-tooltip="Attach image">
                            <Image size={18} />
                        </button>
                        <button className="icon-btn" data-tooltip="Add code snippet">
                            <Code size={18} />
                        </button>
                    </div>
                    <div className="keyboard-hint">
                        Press <kbd>Cmd</kbd> + <kbd>Enter</kbd> to submit
                    </div>
                </div> */}

                {error && <div className="error-message">{error}</div>}
                {error && error.includes('quota exceeded') && (
                    <Link to="/settings/billing" className="btn btn-ghost">Upgrade Plan</Link>
                )}

                {/* Action Buttons */}
                <div className="goal-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={handleSave}
                        disabled={loading}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                        <Save size={16} />
                        Save Goal
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => handleSubmit(false)}
                        disabled={loading}
                    >
                        <Sparkles size={16} />
                        {loading ? 'Generating...' : 'Execute Goal'}
                    </button>
                </div>
            </div>

            {/* Device Picker Modal */}
            {showDevicePicker && (
                <div className="device-picker-overlay" onClick={() => setShowDevicePicker(false)}>
                    <div className="device-picker-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Select Device <PremiumBadge title='Upgrade to run tests on real devices' /></h3>
                            <button className="close-btn" onClick={() => setShowDevicePicker(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <DevicePicker
                            platform={platform.toUpperCase()}
                            onDeviceSelect={handleDeviceSelect}
                            selectedDeviceArn={selectedDevice?.arn}
                        />
                    </div>
                </div>
            )}

            {/* App Picker Modal */}
            {showAppPicker && (
                <div className="device-picker-overlay" onClick={() => setShowAppPicker(false)}>
                    <div className="device-picker-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Select App to Test</h3>
                            <button className="close-btn" onClick={() => setShowAppPicker(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="app-picker">
                            {appsLoading ? (
                                <div className="app-picker-loading">
                                    <Loader2 size={24} className="spinner" />
                                    <p>Loading apps...</p>
                                </div>
                            ) : apps.length === 0 ? (
                                <div className="app-picker-empty">
                                    <Package size={32} />
                                    <p>No apps uploaded yet</p>
                                    <a href="/apps" className="btn btn-secondary">Upload App</a>
                                </div>
                            ) : (
                                <div className="app-list">
                                    {apps.map(app => (
                                        <div
                                            key={app.id}
                                            className={`app-item ${selectedApp?.id === app.id ? 'selected' : ''}`}
                                            onClick={() => handleAppSelect(app)}
                                        >
                                            <div className="app-icon">
                                                {/* <Package size={20} /> */}
                                                {app.metadata?.appInfo?.icon ? (
                                                    <img
                                                        src={app.metadata.appInfo.icon}
                                                        alt={app.displayName || app.name}
                                                        className="apps_listing__app-icon"
                                                    />
                                                ) : (
                                                    <div className="app-icon-fallback">
                                                        {getPlatformIcon(app.platform)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="app-info">
                                                <div className="app-name">{app.displayName || app.name}</div>
                                                <div className="app-details">
                                                    <span className="app-platform">{app.platform}</span>
                                                    {app.version && <span className="app-version">v{app.version}</span>}
                                                    {app.packageName && <span className="app-package">{app.packageName}</span>}
                                                </div>
                                            </div>
                                            {app.fileSize && (
                                                <div className="app-size">{formatFileSize(app.fileSize)}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Example Templates */}
            <div className="templates-section">
                <h3 className="templates-title">
                    <span className="templates-icon">💡</span>
                    TRY THESE EXAMPLES
                </h3>
                <div className="templates-grid">
                    {exampleTemplates.map((template, index) => (
                        <div
                            key={index}
                            className="template-card"
                            onClick={() => handleTemplateClick(template)}
                        >
                            <div className="template-icon">
                                <template.icon size={20} />
                            </div>
                            <h4 className="template-title">{template.title}</h4>
                            <p className="template-desc">{template.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recent Test Runs */}
            {/* <div className="recent-runs">
                <div className="section-header">
                    <h3>Recent Test Runs</h3>
                    <a href="/test-cases" className="view-all-link">View all</a>
                </div>
                <div className="runs-placeholder">
                    <p className="text-muted">No recent test runs yet. Submit a test goal to get started!</p>
                </div>
            </div> */}
        </div>
    );
}

export default TestGoals;
